// ============================================
// ConverseTech Frontend - Elapsed Time Hook
// ============================================

import { useState, useEffect } from 'react';

interface UseElapsedTimeOptions {
  /** Whether the timer is currently active */
  isActive: boolean;
  /** Optional interval in milliseconds (default: 1000ms) */
  intervalMs?: number;
}

interface UseElapsedTimeResult {
  /** Total elapsed time in seconds */
  elapsed: number;
  /** Elapsed time formatted as MM:SS */
  formatted: string;
  /** Reset the timer to zero */
  reset: () => void;
}

/**
 * Custom hook for tracking elapsed time.
 *
 * Starts counting when isActive becomes true, pauses when false.
 * Returns elapsed seconds, formatted string, and reset function.
 *
 * @example
 * const { elapsed, formatted, reset } = useElapsedTime({ isActive: isSessionActive });
 * // elapsed: 125
 * // formatted: "02:05"
 */
export function useElapsedTime({
  isActive,
  intervalMs = 1000,
}: UseElapsedTimeOptions): UseElapsedTimeResult {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!isActive) return;

    const interval = setInterval(() => {
      setElapsed((prev) => prev + 1);
    }, intervalMs);

    return () => clearInterval(interval);
  }, [isActive, intervalMs]);

  const minutes = Math.floor(elapsed / 60);
  const seconds = elapsed % 60;
  const formatted = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;

  const reset = () => setElapsed(0);

  return {
    elapsed,
    formatted,
    reset,
  };
}
